import type { ItemId, Project, ScenarioId } from '@finsim/model';
import { createEvent, createStore, sample } from 'effector';
import { $project, projectClosed, projectShown } from '../project';

/*
 * What the user is looking at: the focused variant (charts, "add item" target) and what the
 * inspector shows.
 */
export type InspectorTarget =
  | { kind: 'item'; itemId: ItemId; scenarioId: ScenarioId }
  | { kind: 'scenario'; scenarioId: ScenarioId }
  | { kind: 'assumptions' }
  | { kind: 'shared' };

export const scenarioFocused = createEvent<ScenarioId>();
export const itemSelected = createEvent<{ itemId: ItemId; scenarioId: ScenarioId }>();
export const scenarioInspected = createEvent<ScenarioId>();
export const assumptionsOpened = createEvent();
export const sharedItemsOpened = createEvent();
export const inspectorClosed = createEvent();

export const $focusedScenarioId = createStore<ScenarioId | null>(null)
  .on(scenarioFocused, (_, id) => id)
  .on(itemSelected, (_, { scenarioId }) => scenarioId)
  .on(scenarioInspected, (_, id) => id)
  .reset(projectClosed);

export const $inspector = createStore<InspectorTarget | null>(null)
  .on(itemSelected, (_, t) => ({ kind: 'item', ...t }))
  .on(scenarioInspected, (_, scenarioId) => ({ kind: 'scenario', scenarioId }))
  .on(assumptionsOpened, () => ({ kind: 'assumptions' }))
  .on(sharedItemsOpened, () => ({ kind: 'shared' }))
  .reset(inspectorClosed, projectClosed, projectShown);

export const $selectedItem = $inspector.map((t) =>
  t?.kind === 'item' ? { itemId: t.itemId, scenarioId: t.scenarioId } : null,
);

/** Keep selection valid as the project changes (deletes, undo, loads). */
function reconcile(project: Project | null, focused: ScenarioId | null): ScenarioId | null {
  if (!project) return null;
  if (focused && project.scenarios.some((s) => s.id === focused)) return focused;
  return project.baselineScenarioId ?? project.scenarios[0]?.id ?? null;
}

sample({
  clock: $project,
  source: $focusedScenarioId,
  fn: (focused, project) => reconcile(project, focused),
  target: $focusedScenarioId,
});

sample({
  clock: $project,
  source: $inspector,
  filter: (target, project) => {
    if (!target || !project) return false;
    if (target.kind === 'assumptions' || target.kind === 'shared') return false;
    const scenario = project.scenarios.find((s) => s.id === target.scenarioId);
    if (!scenario) return true;
    return target.kind === 'item' && !scenario.itemIds.includes(target.itemId);
  },
  target: inspectorClosed,
});
